import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watchEffect, type Ref } from 'vue'

// Нижняя полоса лежит поверх страницы (position: fixed) и сама по себе накрывает
// конец содержимого: у макета внизу заложен отступ только под таб-бар. Полоса
// сообщает макету свою высоту этой переменной, а он добавляет её к нижнему отступу.
//
// Переменная ОДНА на все нижние полосы (установка и уведомления) намеренно: на
// экране полоса всегда одна (арбитраж в AppLayout.vue), а две переменные пришлось
// бы складывать в макете и следить, чтобы вторая обнулялась.
export const BANNER_SPACE_VAR = '--bottom-prompt-space'

// Классы нижней полосы (установка и уведомления) вынесены сюда же намеренно: на
// экране полоса всегда одна (арбитраж в AppLayout.vue), и обе обязаны занимать ровно
// одно и то же место — иначе арбитраж между ними будет заметен визуально (полоса
// «прыгнет»). Раньше строки были продублированы байт в байт в двух компонентах —
// стоило поправить одну и забыть про вторую, и полосы разъехались бы незаметно
// для тестов (Tailwind-классы юнит-тесты не сверяют попиксельно).
//
// z-20 — ниже таб-бара (z-30), поэтому полоса не накрывает навигацию; bottom-14
// поднимает её над таб-баром на мобайле, а на десктопе таб-бара нет (lg:hidden),
// и полоса опускается к нижнему краю. Внешний отступ снизу — под безопасную зону
// iPhone, как у таб-бара.
export const BANNER_SLOT_CLASS =
  'fixed inset-x-0 bottom-14 lg:bottom-4 z-20 mb-[env(safe-area-inset-bottom)] lg:mb-0 px-4'

// Полоса лежит поверх белых карточек, и белой она с ними сливалась: своя подложка
// accent-soft + акцентная рамка и тень делают её отдельным слоем в обеих темах.
export const BANNER_CARD_CLASS = 'rounded-xl border border-accent bg-accent-soft p-3 text-text shadow-lg'

// Реестр вкладов в общую переменную: по одному на каждый смонтированный banner-
// инстанс. Переменная одна на всех, значит и владение общее — иначе последний
// записавший стирает высоту соседа. Каждый вклад отвечает за себя («сколько места
// занимаю сейчас»), а итог всегда считается по всему реестру разом, поэтому
// порядок монтирования и порядок срабатывания эффектов на результат не влияют.
const contributors = new Set<() => number>()

// Пересчёт общий: любой повод (реактивное изменение, resize, размонтирование)
// приводит к одному и тому же — максимуму по реестру. nextTick — чтобы мерить
// высоту уже после того, как Vue обновил разметку.
async function syncAll(): Promise<void> {
  await nextTick()
  if (contributors.size === 0) return

  let height = 0
  for (const contribution of contributors) height = Math.max(height, contribution())
  document.documentElement.style.setProperty(BANNER_SPACE_VAR, `${height}px`)
}

export function useBannerSpace(root: Ref<HTMLElement | null>, active: () => boolean): void {
  const contribution = () => (active() && root.value ? root.value.offsetHeight : 0)
  contributors.add(contribution)

  // Обёртка своя у каждого инстанса: syncAll один на модуль, и подписка напрямую
  // им же схлопнула бы все инстансы в один обработчик — DOM дедуплицирует
  // одинаковые пары (type, callback), и снятие обработчика при уходе ПЕРВОГО
  // инстанса оставило бы остальных без пересчёта по resize.
  const onResize = () => { void syncAll() }

  onMounted(() => {
    // Высота полосы зависит от ширины экрана (на узком текст переносится), поэтому
    // на поворот телефона и смену размера окна пересчитываем заново.
    window.addEventListener('resize', onResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', onResize)
    contributors.delete(contribution)
    // Переменную снимаем совсем, только когда ушёл последний претендент: пока на
    // экране остаётся хоть одна полоса, отступ под неё держать по-прежнему надо.
    if (contributors.size === 0) {
      document.documentElement.style.removeProperty(BANNER_SPACE_VAR)
      return
    }

    void syncAll()
  })

  // watchEffect, а не watch по списку: пересчитать нужно на ЛЮБОЕ изменение,
  // от которого зависит active() и содержимое полосы — видимость, состояние,
  // длина текста. Перечислять их поимённо в каждом компоненте-потребителе значит
  // однажды забыть один и получить отступ не под ту высоту.
  watchEffect(() => {
    active()
    void syncAll()
  })
}

// Арбитраж «полоса против прямого способа на самой странице». Спрашивать об одном
// и том же дважды на одном экране незачем, а ближе к контексту стоит страница: она
// знает, зачем человек сюда пришёл, и говорит об этом своими словами (на «Гостевых
// пропусках» — про дверь). Значит уступает полоса.
//
// Учёт по видам, а не общим счётчиком: уведомления и установка — разные действия,
// и одно другим не заменяется. Плашка про уведомления на «Оповещениях» полосу
// установки не трогает, а блок установки в профиле не отменяет просьбу про пуш —
// на /profile молчат обе полосы, потому что там есть и тумблер, и блок.
//
// Претендентов на вид может быть несколько (плашка и тумблер на одной странице),
// поэтому счётчик: уход первого не должен возвращать полосу, пока второй на экране.
export type PromptKind = 'push' | 'install'

const visiblePagePrompts = reactive<Record<PromptKind, number>>({ push: 0, install: 0 })

export function pagePromptShown(kind: PromptKind): boolean {
  return visiblePagePrompts[kind] > 0
}

// Считается ВИДИМОСТЬ, а не монтирование. Плашка с уже включёнными уведомлениями
// (или в браузере без пуша, или при закрытом флаге раскатки) не рисует ничего —
// уступать ей нечему, и полоса обязана остаться. Плашка пуша (variant === 'plate'
// в PushPrompt.vue) передаёт сюда не саму visible, а `visible.value ||
// !settled.value`: пока состояние пуша ещё не приехало, слот держится и без
// видимого рендера; полный смысл этого — там, а не здесь. Тумблер в профиле
// (PushToggle.vue) держит слот всё время, пока он на экране: он рисует что-то
// всегда — либо переключатель, либо объяснение, почему включить нельзя.
export function usePagePromptPresence(kind: PromptKind, visible: () => boolean): void {
  let counted = false

  function count(value: boolean): void {
    if (value === counted) return

    counted = value
    visiblePagePrompts[kind] += value ? 1 : -1
  }

  watchEffect(() => count(visible()))
  onUnmounted(() => count(false))
}

// Второй арбитраж, отдельный от плашек: слой ПОВЕРХ страницы (сейчас единственный —
// выпадашка колокольчика). Плашка внутри страницы уступает место полосе постранично,
// а открытый слой перекрывает нижнюю полосу физически: она видна под ним, и человек
// получает две одинаковые кнопки «Включить уведомления» разом. Пока слой открыт,
// полосы молчат обе — и пуш, и установка.
//
// Счётчик, а не флаг: колокольчиков на странице два (TopBar на десктопе и мобильная
// шапка), скрытый CSS-ом инстанс живёт своей жизнью, и при смене ширины окна оба
// могут оказаться открытыми. Уход первого не должен возвращать полосу под вторым.
const openOverlays = ref(0)

export const overlayShown = computed(() => openOverlays.value > 0)

export function useOverlayPresence(open: () => boolean): void {
  let counted = false

  function count(value: boolean): void {
    if (value === counted) return

    counted = value
    openOverlays.value += value ? 1 : -1
  }

  watchEffect(() => count(open()))
  // Размонтирование с открытым слоем — обычное дело: внутри выпадашки есть ссылка
  // «Все уведомления», переход по ней уносит компонент вместе с открытой панелью.
  onUnmounted(() => count(false))
}
